import { isEmpty } from 'lodash'
import React, { useEffect, useState } from 'react'
import {
  getGenericEntityListingAPI,
  GetGenericEntityListingApiType,
  getLayoutByIdApi,
  LayoutResponseType,
  SUBFORM_ADD_BUTTON_BEHAVIOR_CONSTANT,
} from '@app-store'
import {
  FieldValues,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import { componentNames, HYBRID_LAYOUT_CONSTANTS } from '../../utils/constants'
import { CustomButton, NAMESPACE, SkeletonLoader, SvgDelete } from '@ui-kit'
import { HybridLayoutDefaultRow } from './HybridLayoutDefaultRow'
import { layoutCache } from './layoutcache'
import {
  buildQuery,
  buildQueryWithParent,
  mergeNestedObjects,
  normalizeLookups,
} from '../../utils/hybridFormRendererfuntions.utils'
import {
  HybridLayoutMultiLookupSubFormPropsType,
  HybridLayoutSubFormPropsType,
  HybridLayoutSubFormWrapperPropsType,
} from './hybridLayout.types'
import { STRINGS } from '../../utils/strings'
import { merge as deepMerge } from 'lodash'

const HybridLayoutSubForm = ({
  recordId,
  fullLayout,
  associatedObject,
  formUniqueId,
  refetchLogic,
  openAllAccordions,
  mode,
}: HybridLayoutSubFormPropsType) => {
  return (
    <HybridLayoutDefaultRow
      layoutData={fullLayout}
      recordId={recordId}
      formUniqueId={formUniqueId}
      isAssociated={true}
      associatedObject={associatedObject}
      refetchLogic={refetchLogic}
      openAllAccordions={openAllAccordions}
      mode={mode}
    />
  )
}

export const HybridLayoutSubFormWrapper = ({
  row,
  recordId,
  fullLayout,
  isAssociated,
  associatedObject,
  formUniqueId,
  refetchLogic,
  openAllAccordions,
  mode,
}: HybridLayoutSubFormWrapperPropsType) => {
  const { getValues, setValue } = useFormContext()
  const [layoutObject, setLayoutObject] = useState<LayoutResponseType | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(!layoutObject)
  const [error, setError] = useState<boolean>(false)

  const associatedObj = {
    ...associatedObject,
    associatedPath: isAssociated
      ? `${associatedObject?.associatedPath}.${HYBRID_LAYOUT_CONSTANTS?.NESTING_DATA_KEY}.${row?.lookup_meta?.lookup_keyName}`
      : `${HYBRID_LAYOUT_CONSTANTS?.NESTING_DATA_KEY}.${row?.lookup_meta?.lookup_keyName}`,
    lookupKeyName: associatedObject?.lookupKeyName ? `${associatedObject?.lookupKeyName}.${row?.lookup_meta?.lookup_keyName}` : row?.lookup_meta?.lookup_keyName,
  }
  const field =
    fullLayout?.layout_data?.added_fields?.[
      row?.lookup_meta?.lookup_keyName ?? HYBRID_LAYOUT_CONSTANTS.ERROR_KEY
    ]

  const fieldPath = isAssociated
    ? associatedObject?.associatedPath
    : row?.lookup_meta?.lookup_keyName
  const fieldValue = useWatch({
    name:
      field?.ui_data_type === componentNames.MULTI_LOOKUP
        ? HYBRID_LAYOUT_CONSTANTS.UNWATCH_FIELD_KEY
        : fieldPath + '.id',
  })

  const layoutId = field?.create_layout_id || ''
  const entityId = field?.relationship?.target_entity_id || ''

  const fetchData = async (payload: FieldValues, dataId: string) => {
    if (!dataId) return

    try {
      const response = await getGenericEntityListingAPI(
        payload as GetGenericEntityListingApiType,
        dataId
      )
      if (response) {
        const normalized = normalizeLookups(response)
        const val = getValues()
        let endData = val
        if (field?.ui_data_type === componentNames.MULTI_LOOKUP) {
          endData = deepMerge(normalized, val)
        } else {
          endData = mergeNestedObjects(
            val,
            normalized,
            fieldPath ?? HYBRID_LAYOUT_CONSTANTS.ERROR_KEY
          )
        }
        if (
          endData?.[HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY]?.[fieldPath ?? '']
        ) {
          setValue(
            `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${fieldPath}`,
            endData?.[HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY]?.[
              fieldPath ?? ''
            ]
          )
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  useEffect(() => {
    let active = true

    if (isEmpty(field) || !layoutId || !entityId) {
      setLayoutObject(null)
      setIsLoading(false)
      return
    }

    const key = `${entityId}:${layoutId}`

    const fetchLayout = async () => {
      setIsLoading(true)
      setError(false)
      try {
        const response = await getLayoutByIdApi({
          entityId: String(entityId),
          layoutId: String(layoutId),
        })
        if (!active) return
        if (response?.data) {
          layoutCache.set(key, response.data)
          setLayoutObject(response.data)
        } else {
          setLayoutObject(null)
        }
      } catch (e) {
        if (active) {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch layout', e)
          setError(true)
          setLayoutObject(null)
        }
      } finally {
        if (active) setIsLoading(false)
      }
    }

    fetchLayout()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (
      field?.ui_data_type === componentNames.MULTI_LOOKUP &&
      layoutObject &&
      recordId
    ) {
      const data = buildQueryWithParent(
        fullLayout?.entity_id?.toString() || '',

        [
          {
            isAssociated: false,
            addedFields: layoutObject?.layout_data?.added_fields ?? {},
          },
        ],
        {
          parentKey: row?.lookup_meta?.lookup_keyName,
        }
      )
      fetchData(data, recordId)
    } else if (layoutObject && fieldValue) {
      const data = buildQuery(entityId?.toString(), [
        {
          isAssociated: false,
          addedFields: layoutObject?.layout_data?.added_fields ?? {},
        },
      ])
      fetchData(data, fieldValue)
    }
  }, [fieldValue, layoutObject, recordId])

  if (isLoading) return <SkeletonLoader rows={4} columns={4} />
  if (error || isEmpty(layoutObject))
    return <div>{STRINGS.NO_LAYOUT_FOUND}</div>

  if (isEmpty(field)) {
    return 'null'
  }

  return field?.ui_data_type === componentNames.MULTI_LOOKUP ? (
    <HybridLayoutMultiLookupSubForm
      row={row}
      recordId={recordId}
      fullLayout={layoutObject}
      isAssociated={true}
      associatedObject={associatedObj}
      formUniqueId={formUniqueId}
      refetchLogic={refetchLogic}
      openAllAccordions={openAllAccordions}
      mode={mode}
    />
  ) : (
    <HybridLayoutSubForm
      recordId={recordId}
      fullLayout={layoutObject}
      isAssociated={true}
      associatedObject={associatedObj}
      formUniqueId={formUniqueId}
      refetchLogic={refetchLogic}
      openAllAccordions={openAllAccordions}
      mode={mode}
    />
  )
}

export const HybridLayoutMultiLookupSubForm = ({
  row,
  recordId,
  fullLayout,
  isAssociated,
  associatedObject,
  formUniqueId,
  refetchLogic,
  mode,
  openAllAccordions,
}: HybridLayoutMultiLookupSubFormPropsType) => {
  const { control, getValues, setValue } = useFormContext()
  const { fields, append, remove } = useFieldArray({
    control,
    name: associatedObject?.associatedPath ?? HYBRID_LAYOUT_CONSTANTS.ERROR_KEY,
  })
  const [show, setShow] = useState(
    mode === 'edit' &&
      row?.add_button_behavior ===
        SUBFORM_ADD_BUTTON_BEHAVIOR_CONSTANT.ENABLE_ADD
  )
  const [activeItems, setActiveItems] = useState<number>(0)
  const watch = useWatch({
    name:
      associatedObject?.associatedPath ??
      HYBRID_LAYOUT_CONSTANTS.UNWATCH_FIELD_KEY,
  })

  const handleDelete = (index: number) => {
    const path =
      associatedObject?.associatedPath ?? HYBRID_LAYOUT_CONSTANTS.ERROR_KEY

    const array: FieldValues = getValues(path) ?? []

    const item = array[index]
    if (item?.id) {
      const updatedArray = array?.map((el: FieldValues, idx: number) =>
        idx === index ? { ...el, _state: NAMESPACE?.REMOVED } : el
      )
      setValue(path, updatedArray)
    } else {
      remove(index)
    }
  }

  useEffect(() => {
    if (mode === 'view') {
      setShow(false)
      return
    }
    const activeCount = watch?.filter(
      (el: FieldValues) => el?._state !== NAMESPACE?.REMOVED
    )?.length
    setActiveItems(activeCount)
    if (
      watch === undefined ||
      (row?.add_button_behavior ===
        SUBFORM_ADD_BUTTON_BEHAVIOR_CONSTANT.ENABLE_ADD &&
        activeCount < 1)
    ) {
      append({})
    } else if (
      row?.add_button_behavior ===
        SUBFORM_ADD_BUTTON_BEHAVIOR_CONSTANT.DISABLED_ADD &&
      row?.initial_count &&
      activeCount < row?.initial_count
    ) {
      const leftLength = row?.initial_count - (activeCount || 0)
      const val = [...watch, ...Array(leftLength).fill({})]
      setValue(
        associatedObject?.associatedPath ?? HYBRID_LAYOUT_CONSTANTS.ERROR_KEY,
        val
      )
    }
    if (activeCount >= (row?.initial_count ?? 1)) {
      setShow(false)
      return
    } else {
      setShow(true)
      return
    }
  }, [watch])

  return (
    <div>
      {fields?.map((item: FieldValues, index) => {
        if (item?._state === NAMESPACE?.REMOVED) return null
        return (
          <div key={item?.id}>
            <div className="flex gap-2 justify-end my-2">
              {activeItems > 1 &&
                mode !== 'view' &&
                row?.add_button_behavior ===
                  SUBFORM_ADD_BUTTON_BEHAVIOR_CONSTANT.ENABLE_ADD && (
                  <div className="flex gap-2 justify-end my-2">
                    <CustomButton onClick={() => handleDelete(index)}>
                      <SvgDelete />
                    </CustomButton>
                  </div>
                )}
            </div>
            <div key={item.id}>
              <HybridLayoutSubForm
                recordId={recordId}
                fullLayout={fullLayout}
                isAssociated={isAssociated}
                associatedObject={{
                  ...associatedObject,
                  associatedPath:
                    associatedObject?.associatedPath + `.${index}`,
                  isSubForm: true,
                  subFormParentPath:
                    associatedObject?.associatedPath + `.${index}`,
                }}
                formUniqueId={formUniqueId}
                refetchLogic={refetchLogic}
                mode={mode}
                openAllAccordions={openAllAccordions}
              />
            </div>
          </div>
        )
      })}

      {show ? (
        <div className="flex gap-2 justify-end my-2">
          <CustomButton
            onClick={() => {
              if (activeItems < (row?.initial_count ?? 1)) {
                append({})
              } else {
                setShow(false)
              }
            }}
          >
            ADD
          </CustomButton>
        </div>
      ) : null}
    </div>
  )
}

export default HybridLayoutSubForm
